import React, { useState } from 'react';
import { TrendingUp, Download, BarChart3, PlayCircle, UserX, Users, Settings, FileText, Menu, X, LogOut } from 'lucide-react';
import EmployeePhoto from './EmployeePhoto';
import { resolvePhotoUrl } from '../services/api';
import type { RankingsViewProps, TabType } from '../types';

const RankingsView: React.FC<RankingsViewProps> = ({
  rankings,
  activeRound,
  userComparisons,
  unknownEmployeeCount,
  userRole,
  hasMorePairs,
  userName,
  userEmail,
  userPhotoURL,
  onLogout,
  onBackToComparisons,
  onExportData,
  onNavigate
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Count actual completed comparisons (choice + tie); skipped/unknown are excluded
  const completedCount = userComparisons.filter(c => c.comparisonType !== 'skipped' && c.comparisonType !== 'unknown').length;

  const navTabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: BarChart3 },
    { id: 'rounds' as TabType, label: 'Evaluation Rounds', icon: PlayCircle },
    { id: 'rankings' as TabType, label: 'Rankings', icon: TrendingUp },
    { id: 'skipped' as TabType, label: 'Skipped', icon: UserX },
    ...(userRole === 'admin' ? [
      { id: 'employees' as TabType, label: 'Employees', icon: Users },
      { id: 'settings' as TabType, label: 'Settings', icon: Settings },
      { id: 'auditlog' as TabType, label: 'Audit Log', icon: FileText },
    ] : []),
  ] as { id: TabType; label: string; icon: React.ComponentType<{ className?: string }> }[];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-2 sm:p-4">
      <div className="max-w-3xl mx-auto">
        {/* Header + Navigation: consistent with dashboard */}
        {(() => {
          const userInitials = userName
            ? userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
            : '?';
          const resolvedPhoto = resolvePhotoUrl(userPhotoURL);

          const profileBlock = (
            <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
              <div className="text-right hidden md:block">
                <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{userName}</p>
                <p className="text-xs text-gray-500 truncate max-w-[150px]">{userEmail}</p>
              </div>
              {resolvedPhoto ? (
                <img
                  src={resolvedPhoto}
                  alt={userName}
                  className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover shadow flex-shrink-0"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style'); }}
                />
              ) : null}
              <div
                className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-xs md:text-sm shadow flex-shrink-0"
                style={resolvedPhoto ? { display: 'none' } : undefined}
              >
                {userInitials}
              </div>
              <button
                onClick={onLogout}
                className="flex items-center gap-1 px-2 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors min-h-[44px] min-w-[44px] justify-center"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden md:inline">Sign Out</span>
              </button>
            </div>
          );

          return (
            <div className="bg-white rounded-xl shadow-md px-2 sm:px-4 py-2 mb-3 border border-gray-100">
              {/* Mobile: hamburger + label | profile + logout */}
              <div className="md:hidden flex items-center justify-between">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-sm text-gray-900">Rankings</span>
                </button>
                {profileBlock}
              </div>
              {/* Mobile: dropdown menu */}
              {mobileMenuOpen && (
                <div className="md:hidden pb-2 border-t border-gray-100 mt-1">
                  <nav className="flex flex-col gap-1 pt-2">
                    {navTabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => { onNavigate(tab.id); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg transition-all min-h-[44px] text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      >
                        <tab.icon className="w-5 h-5" />
                        <span className="font-medium text-sm">{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              )}
              {/* Desktop: tabs + profile in one row */}
              <div className="hidden md:flex items-center justify-between">
                <div className="flex items-center gap-0.5">
                  {navTabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => onNavigate(tab.id)}
                      className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium cursor-pointer whitespace-nowrap flex-shrink-0 min-h-[44px]"
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
                {profileBlock}
              </div>
            </div>
          );
        })()}

        <div className="bg-white rounded-2xl shadow-2xl border border-gray-100">
          {/* Responsive: header stacks on mobile, side-by-side on wider screens */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-md flex-shrink-0">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                  {hasMorePairs ? 'Current Rankings' : 'Rankings Complete!'}
                </h1>
                <p className="text-xs text-gray-500 leading-tight">
                  <span className="font-semibold text-blue-600">{completedCount}</span> comparison{completedCount !== 1 ? 's' : ''}
                  {unknownEmployeeCount > 0 && <span className="text-orange-500"> · {unknownEmployeeCount} skipped</span>}
                  {hasMorePairs ? ' so far' : ''}
                  {activeRound && (
                    <span className="ml-2">
                      <span className={`inline-block w-1.5 h-1.5 rounded-full align-middle mr-1 ${activeRound.status === 'completed' ? 'bg-blue-500' : 'bg-green-500'}`}></span>
                      <span className="truncate max-w-xs inline-block align-bottom" title={activeRound.roundName}>{activeRound.roundName}</span>
                    </span>
                  )}
                </p>
              </div>
            </div>
            {/* Responsive: buttons wrap on small screens */}
            <div className="flex gap-2 flex-shrink-0 flex-wrap">
              {hasMorePairs && (
                <button
                  onClick={onBackToComparisons}
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 active:scale-95 transition-all duration-150 shadow-md text-sm font-medium cursor-pointer whitespace-nowrap min-h-[44px]"
                >
                  Continue Comparisons
                </button>
              )}
              {userRole === 'admin' && (
                <button
                  onClick={onExportData}
                  className="flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 active:scale-95 transition-all duration-150 shadow-md text-sm font-medium cursor-pointer whitespace-nowrap min-h-[44px]"
                >
                  <Download className="w-4 h-4" />
                  Export Rankings
                </button>
              )}
            </div>
          </div>

          {/* Responsive: ranking list padding scales */}
          <div className="p-2 sm:p-4 space-y-1.5">
            {rankings.map((emp) => (
              <div
                key={emp.employeeID}
                className={`flex items-center gap-2 sm:gap-3 px-2 sm:px-4 py-2.5 rounded-lg transition-all ${
                  emp.rank === 1
                    ? 'bg-gradient-to-r from-yellow-50 to-yellow-100 border border-yellow-300'
                    : emp.rank === 2
                    ? 'bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-300'
                    : emp.rank === 3
                    ? 'bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-300'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-full font-bold text-sm flex-shrink-0 ${
                    emp.rank === 1
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-500 text-yellow-900'
                      : emp.rank === 2
                      ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-900'
                      : emp.rank === 3
                      ? 'bg-gradient-to-br from-orange-400 to-orange-500 text-orange-900'
                      : 'bg-white text-gray-700 border border-gray-300'
                  }`}
                >
                  {emp.rank}
                </span>
                <EmployeePhoto photoURL={emp.photoURL} name={emp.name} size="small" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm leading-tight truncate">{emp.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {emp.title}{emp.department ? ` · ${emp.department}` : ''}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent leading-tight">
                    {emp.eloRating}
                  </p>
                  <p className="text-xs text-gray-400">Elo</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RankingsView;
